import { Button, Checkbox, NativeSelect, Select } from '@common/components';
import X from '@geist-ui/icons/x';
import { Flex, Title, UnstyledButton } from '@mantine/core';
import classNames from 'classnames';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { IReportSettings, updateReportSettings, useAppDispatch } from 'src/store';

import styles from './styles.module.scss';
import { useParams } from 'react-router-dom';
import { frequencyMap } from 'src/constants/automatedSettings';
import { useTranslation } from 'react-i18next';
import i18n from '@common/i18n/config';
import { useOs } from '@mantine/hooks';

interface AutomatedSettingsProps {
  reportSettings: IReportSettings;
  setShowSettings: () => void;
  showSettings: boolean;
  auditEngines: Record<string, string>[] | null;
}

interface ISelectData {
  value: string;
  label: string;
}

const AutomatedSettingsMobile: FC<AutomatedSettingsProps> = ({
  reportSettings,
  setShowSettings,
  showSettings,
  auditEngines,
}): JSX.Element => {
  const os = useOs();
  const [t] = useTranslation('automatedReports');
  const [c] = useTranslation('common');
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [frequency, setFrequency] = useState<string | null>(
    reportSettings ? reportSettings.frequency : frequencyMap[0].value,
  );
  const [auditEngine, setAuditEngine] = useState<string | null>(reportSettings ? reportSettings.engine_id : null);
  const [notifications, setNotifications] = useState<boolean>(reportSettings ? reportSettings.notifications : false);
  const [auditEnginesData, setAuditEnginesData] = useState<ISelectData[]>([]);

  useEffect(() => {
    if (auditEngines) {
      const data: ISelectData[] = auditEngines.map((el) => ({ label: `${el.name} ${el.compliance_level} ${el?.deviceName ? el.deviceName : ''} ${el.browserName}`, value: el.id }));
      setAuditEnginesData(data);
    }
  }, [auditEngines]);

  const handleStatusChange = (value: string | null): void => {
    setFrequency(value);
  };
  const handleAuditEngine = (value: string | null): void => {
    setAuditEngine(value);
  };

  const handleNativeStatusChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setFrequency(e.currentTarget.value);
  };

  const handleNativeAuditEngine = (e: ChangeEvent<HTMLSelectElement>): void => {
    setAuditEngine(e.currentTarget.value);
  };

  const updateSettings = (): void => {
    const data = Object.assign({}, reportSettings, { frequency, notifications });
    dispatch(updateReportSettings({ data, id }));
  };

  const [frequencyData, setFrequencyData] = useState<ISelectData[]>([]);

  useEffect(() => {
    const setValues = (): void => {
      const newStrings = frequencyMap.map((e) => ({ ...e, label: t(e.label) }));
      setFrequencyData(newStrings);
    };

    setValues();

    i18n.on('languageChanged', setValues);

    return () => {
      i18n.off('languageChanged', setValues);
    };
  }, []);

  return (
    <Flex className={classNames(styles['settings-wrapper'])} direction={'column'} w={'100%'}>
      <Flex justify={'space-between'} align={'center'}>
        <Title className={styles['second-heading']} mb={'8px'} order={2}>
          {t('automatedAuditSettings')}
        </Title>
        <UnstyledButton
          ml={'16px'}
          className={classNames(styles['action-btn'])}
          aria-label={`${t('cancelSettings')}`}
          onClick={() => {
            setShowSettings();
          }}>
          <X />
        </UnstyledButton>
      </Flex>

      <Flex direction={'column'}>
        <Flex direction={'column'} gap={'16px'}>
          {os === 'ios' || os === 'macos' ? (
            <>
              <NativeSelect
                variant="secondary"
                withBorder
                aria-label={`${c('combobox')}`}
                label={`${t('defineScanFrequency')}`}
                value={frequency as string}
                data={frequencyData}
                onChange={handleNativeStatusChange}
              />
              <NativeSelect
                variant="secondary"
                withBorder
                aria-label={`${c('combobox')}`}
                label={`${t('auditEngine')}`}
                value={auditEngine as string}
                data={auditEnginesData}
                onChange={handleNativeAuditEngine}
              />
            </>
          ) : (
            <>
              <Select
                variant="secondary"
                withBorder
                withIcon
                aria-label={`${c('combobox')}`}
                label={`${t('defineScanFrequency')}`}
                value={frequency}
                data={frequencyData}
                onChange={handleStatusChange}
              />
              <Select
                variant="secondary"
                withBorder
                withIcon
                aria-label={`${c('combobox')}`}
                label={`${t('auditEngine')}`}
                value={auditEngine}
                data={auditEnginesData}
                onChange={handleAuditEngine}
              />
            </>
          )}

          <Checkbox
            className={styles.notifyCheckbox}
            label={`${t('notifyMeOnEmail')}`}
            checked={notifications}
            onChecked={(e) => setNotifications(e.target.checked)}
          />
        </Flex>

        <Button
          onClick={() => {
            updateSettings();
            setShowSettings();
          }}
          aria-label={`${t('confirmSettings')}`}>
          {t('Save')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default AutomatedSettingsMobile;
